import { useParams, Link } from 'react-router-dom';
import { getArticleById, getRelatedArticles, getCategoryBySlug } from '@/mocks/articles';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '0', 10);
  const article = getArticleById(articleId);

  if (!article) {
    return <NotFound />;
  }

  const category = getCategoryBySlug(article.category);
  const related = getRelatedArticles(articleId, article.category, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '91n官方',
    },
  };

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: '分类浏览', to: '/category' },
    ...(category ? [{ label: category.name, to: `/category?tab=${category.slug}` }] : []),
    { label: article.title },
  ];

  return (
    <main className="min-h-screen pt-16">
      <script type="application/ld+json">
        {JSON.stringify(articleSchema)}
      </script>

      <div className="w-full max-w-6xl mx-auto px-4 md:px-6 pt-4">
        <nav aria-label="面包屑导航" className="py-3">
          <ol className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            {breadcrumbItems.map((item, index) => (
              <li key={index} className="flex items-center gap-2">
                {index > 0 && <span className="text-foreground-300">/</span>}
                {item.to ? (
                  <Link to={item.to} className="hover:text-primary-500 transition-colors duration-200">
                    {item.label}
                  </Link>
                ) : (
                  <span className="text-foreground-900 font-medium">{item.label}</span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      </div>

      <article className="w-full max-w-3xl mx-auto px-4 md:px-6 pb-16">
        <div className="mb-8">
          <div className="flex items-center gap-3 text-xs text-foreground-400 mb-4 flex-wrap">
            {category && (
              <Link
                to={`/category?tab=${category.slug}`}
                className="bg-primary-100 text-primary-700 px-3 py-1 rounded-full hover:bg-primary-200 transition-colors duration-200"
              >
                {category.name}
              </Link>
            )}
            <span>{article.publishDate}</span>
            <span>·</span>
            <span>{article.readTime} 分钟阅读</span>
            <span>·</span>
            <span>{article.author}</span>
          </div>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-serif text-foreground-900 leading-tight mb-4">
            {article.title}
          </h1>
          <p className="text-base text-foreground-500 leading-relaxed">
            {article.excerpt}
          </p>
        </div>

        <div className="w-full h-64 md:h-80 rounded-lg overflow-hidden mb-10">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover object-top"
            title={`${article.title} - 91n`}
          />
        </div>

        <div
          className="prose prose-sm md:prose-base max-w-none"
          dangerouslySetInnerHTML={{
            __html: article.content
              .replace(/<h2>/g, '<h2 class="text-xl md:text-2xl font-bold font-serif text-foreground-900 mt-10 mb-4">')
              .replace(/<h3>/g, '<h3 class="text-lg md:text-xl font-semibold font-serif text-foreground-800 mt-8 mb-3">')
              .replace(/<p>/g, '<p class="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">')
              .replace(/<ul>/g, '<ul class="list-disc pl-5 mb-4 space-y-2">')
              .replace(/<ol>/g, '<ol class="list-decimal pl-5 mb-4 space-y-2">')
              .replace(/<li>/g, '<li class="text-sm md:text-base text-foreground-700 leading-relaxed">')
              .replace(/<strong>/g, '<strong class="font-semibold text-foreground-900">'),
          }}
        />

        <div className="mt-12 pt-8 border-t border-background-200">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="text-sm text-foreground-500">
                作者：<span className="text-foreground-900 font-medium">{article.author}</span>
              </p>
              <p className="text-xs text-foreground-400 mt-1">
                发布于 {article.publishDate} · 阅读时间约 {article.readTime} 分钟
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 hover:text-primary-500 text-foreground-500 transition-colors duration-200 cursor-pointer">
                <i className="ri-heart-line text-sm" />
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 hover:text-primary-500 text-foreground-500 transition-colors duration-200 cursor-pointer">
                <i className="ri-bookmark-line text-sm" />
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 hover:text-primary-500 text-foreground-500 transition-colors duration-200 cursor-pointer">
                <i className="ri-share-forward-line text-sm" />
              </button>
            </div>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="bg-background-100 py-14">
          <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
            <h2 className="text-xl font-bold font-serif text-foreground-900 mb-8">
              相关文章
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((ra) => (
                <Link
                  key={ra.id}
                  to={`/article/${ra.id}`}
                  className="group bg-background-50 rounded-lg overflow-hidden hover:bg-background-200/50 transition-colors duration-200 cursor-pointer"
                >
                  <div className="w-full h-40 overflow-hidden">
                    <img
                      src={ra.image}
                      alt={ra.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      title={`${ra.title} - 91n`}
                    />
                  </div>
                  <div className="p-4">
                    <div className="text-xs text-foreground-400 mb-2">{ra.publishDate}</div>
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                      {ra.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>

            <div className="text-center mt-8">
              <Link
                to={`/category?tab=${article.category}`}
                className="inline-flex items-center gap-2 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors duration-200"
              >
                查看更多 {category?.name || ''} 文章
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </section>
      )}
    </main>
  );
}